import { HELPDESK_STATUSES } from "@seagames/contracts";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertIcon, InfoIcon } from "../transport/icons";
import type { HelpdeskPriority, HelpdeskStatus } from "./types";

/**
 * Ticket status and priority, her colours (store-l3.js `TK_ST`, admin.css
 * `.l3pri--*`): Open orange, In progress blue, Resolved green — a dot and the
 * label on a soft fill (`.dchip`), ink dark enough for 4.5:1 on it. The
 * Statuses guide reads the same `TICKET_STATUS_TONE`, so a chip and its guide
 * entry can never disagree.
 */
export const TICKET_STATUS_TONE: Record<HelpdeskStatus, { dot: string; bg: string; ink: string }> = {
	OPEN: { dot: "#e07b00", bg: "#fcefdf", ink: "#8a4400" },
	IN_PROGRESS: { dot: "#2f62d9", bg: "#e1eafe", ink: "#1d4ed8" },
	RESOLVED: { dot: "#17804a", bg: "#e1f3e8", ink: "#17663a" },
};

export function TicketStatusChip({ status, className = "" }: { status: HelpdeskStatus; className?: string }) {
	const { t } = useTranslation("helpdesk");
	const tone = TICKET_STATUS_TONE[status];
	return (
		<span
			className={`inline-flex h-[1.375rem] shrink-0 items-center gap-[0.3125rem] rounded-[0.3125rem] bg-[var(--hd-bg)] px-[0.4375rem] text-[0.71875rem] font-semibold whitespace-nowrap text-[var(--hd-ink)] ${className}`}
			style={{ "--hd-bg": tone.bg, "--hd-ink": tone.ink } as CSSProperties}
			title={t(`status.hint.${status}`)}
		>
			<span aria-hidden className="size-[0.4375rem] rounded-full" style={{ background: tone.dot }} />
			{t(`status.label.${status}`)}
		</span>
	);
}

/** The status chips of a list toolbar: her order, with the coloured dot. */
export function useStatusChipOptions() {
	const { t } = useTranslation("helpdesk");
	return HELPDESK_STATUSES.map((s) => ({
		value: s,
		label: t(`status.label.${s}`),
		icon: <span aria-hidden className="inline-block size-[0.5rem] rounded-full" style={{ background: TICKET_STATUS_TONE[s].dot }} />,
	}));
}

const PRIORITY_TILE: Record<HelpdeskPriority, string> = {
	URGENT: "bg-gms-bad-soft text-gms-bad",
	HIGH: "bg-gms-bad-soft text-gms-bad",
	MEDIUM: "bg-gms-warn-soft text-gms-warn",
	LOW: "bg-gms-navy-soft text-gms-navy",
};

/** Her `.l3ep__ic.l3pri--*` tile: a warning sign for Urgent / High, an "i" otherwise. `icon` overrides the glyph (the NOC list shows a bed). */
export function PriorityTile({ priority, icon, className = "" }: { priority: HelpdeskPriority; icon?: ReactNode; className?: string }) {
	const { t } = useTranslation("helpdesk");
	return (
		<span
			className={`grid size-[2.25rem] shrink-0 place-items-center rounded-[0.5rem] [&>svg]:size-[1.0625rem] ${PRIORITY_TILE[priority]} ${className}`}
			title={t(`priority.label.${priority}`)}
		>
			{icon ?? (priority === "URGENT" || priority === "HIGH" ? <AlertIcon /> : <InfoIcon />)}
		</span>
	);
}
